import React, { createContext, forwardRef, useCallback, useContext, useState } from 'react';
import styles from './Sidebar.module.css';

/* ─────────────────────────── Context ─────────────────────────── */

interface SidebarContextValue {
  collapsed: boolean;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  toggle: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

/* ─────────────────────────── cx helper ─────────────────────────── */

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ─────────────────────────── Types ─────────────────────────── */

export interface SidebarProps extends Omit<React.ComponentPropsWithoutRef<'aside'>, 'onChange'> {
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  collapsible?: boolean;
}

export interface SidebarHeaderProps extends React.ComponentPropsWithoutRef<'div'> {}
export interface SidebarNavProps    extends React.ComponentPropsWithoutRef<'nav'>  {}
export interface SidebarFooterProps extends React.ComponentPropsWithoutRef<'div'> {}

export interface SidebarItemProps extends React.ComponentPropsWithoutRef<'button'> {
  icon?:         React.ReactNode;
  active?:       boolean;
  badge?:        React.ReactNode;
  badgeTooltip?: string;
}

export interface SidebarSeparatorProps extends React.ComponentPropsWithoutRef<'div'> {
  label?: string;
}

/* ─────────────────────────── Sidebar (root) ─────────────────────────── */

const SidebarRoot = forwardRef<HTMLElement, SidebarProps>(
  ({ collapsed: collapsedProp, defaultCollapsed = false, onCollapse, collapsible = false, className, children, ...rest }, ref) => {
    const [collapsedState, setCollapsedState] = useState(defaultCollapsed);
    const isControlled = collapsedProp !== undefined;
    const collapsed    = isControlled ? collapsedProp! : collapsedState;

    const handleToggle = useCallback(() => {
      const next = !collapsed;
      if (!isControlled) setCollapsedState(next);
      onCollapse?.(next);
    }, [collapsed, isControlled, onCollapse]);

    return (
      <SidebarContext.Provider value={{ collapsed, toggle: handleToggle }}>
        <aside ref={ref} className={cx(styles.sidebar, collapsed && styles.collapsed, className)} {...rest}>
          {children}
        </aside>
      </SidebarContext.Provider>
    );
  },
);

SidebarRoot.displayName = 'Sidebar';

/* ─────────────────────────── Sidebar.Header ─────────────────────────── */

const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.header, className)} {...rest}>
      {children}
    </div>
  ),
);

SidebarHeader.displayName = 'Sidebar.Header';

/* ─────────────────────────── Sidebar.Nav ─────────────────────────── */

const SidebarNav = forwardRef<HTMLElement, SidebarNavProps>(
  ({ className, children, ...rest }, ref) => (
    <nav ref={ref} className={cx(styles.nav, className)} {...rest}>
      {children}
    </nav>
  ),
);

SidebarNav.displayName = 'Sidebar.Nav';

/* ─────────────────────────── SidebarItem ─────────────────────────── */

const SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  ({ icon, active = false, badge, badgeTooltip, disabled, className, children, title, ...rest }, ref) => {
    const { collapsed } = useSidebar();
    const label = typeof children === 'string' ? children : title;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        title={collapsed ? (label as string | undefined) : undefined}
        aria-current={active ? 'page' : undefined}
        className={cx(styles.item, active && styles.itemActive, className)}
        {...rest}
      >
        {icon && <span className={styles.itemIcon}>{icon}</span>}
        {!collapsed && children && <span className={styles.itemLabel}>{children}</span>}
        {!collapsed && badge != null && (
          badgeTooltip ? (
            <span className={styles.itemBadgeWrapper} data-tooltip={badgeTooltip}>
              <span className={styles.itemBadge}>{badge}</span>
            </span>
          ) : (
            <span className={styles.itemBadge}>{badge}</span>
          )
        )}
      </button>
    );
  },
);

SidebarItem.displayName = 'Sidebar.Item';

/* ─────────────────────────── Sidebar.Separator ─────────────────────────── */

const SidebarSeparator = forwardRef<HTMLDivElement, SidebarSeparatorProps>(
  ({ label, className, ...rest }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div ref={ref} className={cx(styles.separator, className)} {...rest}>
        {!collapsed && label
          ? <span className={styles.separatorLabel}>{label}</span>
          : <hr className={styles.separatorLine} />
        }
      </div>
    );
  },
);

SidebarSeparator.displayName = 'Sidebar.Separator';

/* ─────────────────────────── Sidebar.Footer ─────────────────────────── */

const SidebarFooter = forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.footer, className)} {...rest}>
      {children}
    </div>
  ),
);

SidebarFooter.displayName = 'Sidebar.Footer';

/* ─────────────────────────── Compound export ─────────────────────────── */

const Sidebar = Object.assign(SidebarRoot, {
  Header:    SidebarHeader,
  Nav:       SidebarNav,
  Item:      SidebarItem,
  Separator: SidebarSeparator,
  Footer:    SidebarFooter,
});

export { Sidebar, SidebarItem };
